import get from "lodash/get";
import * as React from "react";
import { connect } from "react-redux";

import {
  Coupons as CouponsComponent,
  CouponsProps,
} from "../../components/Coupons/Coupons";
import { getCouponsAction, getRewardsAction } from "../../actions";
import { StoreState } from "../../reducers";
import { getCoupons, getRewards, isGetRewardsLoading } from "../../reducers/reward/methods";
import { getWidgetConfig } from "../../reducers/methods";
import { customerAuthenticationOccurred } from "../../utils/customer";

import { loyaltyDashboardFilteredRewardsAndCoupons } from "../MyRewards/filterLoyaltyRewards";

interface CouponsContainerProps extends CouponsProps {
  getCouponsAction: typeof getCouponsAction;
  getRewardsAction: typeof getRewardsAction;
  coupons: ReturnType<typeof getCoupons>;
  // settings.rewardStoreWidgetId being set means this widget instance IS the
  // Reward Store widget—all activity is relevant there, so skip filtering.
  // When absent the widget is the Loyalty Dashboard; reward-store entries are
  // filtered out to keep it focused on loyalty-only activity.
  rewardStoreWidgetId?: string;
  rewardsLoading: boolean;
}

class Coupons extends React.Component<CouponsContainerProps> {
  private fetchData(authenticated: boolean) {
    if (!authenticated) {
      return;
    }
    this.props.getCouponsAction();
    // On the loyalty dashboard (no rewardStoreWidgetId) we filter coupons using
    // the rewards list. Fetch rewards here so this component is self-sufficient
    // even when the MyRewards widget is absent from the page.
    if (!this.props.rewardStoreWidgetId && !this.props.rewardsLoading) {
      this.props.getRewardsAction();
    }
  }

  componentDidMount() {
    this.fetchData(get(this.props.data, "customer.authenticated", false));
  }

  componentDidUpdate(prevProps: CouponsContainerProps) {
    if (customerAuthenticationOccurred(prevProps.data, this.props.data)) {
      this.fetchData(true);
    }
  }

  render() {
    return <CouponsComponent {...this.props} />;
  }
}

const mapStateToProp = (state: StoreState) => {
  const config = getWidgetConfig(state);
  const rewardStoreWidgetId = get(config, "settings.rewardStoreWidgetId") as
    | string
    | undefined;
  const rawRewards = getRewards(state.reward);
  const rawCoupons = getCoupons(state.reward);
  const { coupons: filteredCoupons } = loyaltyDashboardFilteredRewardsAndCoupons({
    rewardStoreWidgetId,
    rewards: rawRewards ?? undefined,
    coupons: rawCoupons ?? undefined,
  });

  // While rewards are loading on the loyalty dashboard, hold coupons as null
  // (renders as an empty list) so reward-store coupons don't flash briefly
  // before being filtered out once the rewards list arrives.
  // Because this component self-fetches rewards (see fetchData), this gate is
  // guaranteed to resolve—it does not depend on MyRewards being on the page.
  const rewardsCurrentlyLoading = isGetRewardsLoading(state.reward);
  const coupons =
    !rewardStoreWidgetId && rewardsCurrentlyLoading ? null : filteredCoupons;

  return {
    coupons,
    rewardStoreWidgetId,
    rewardsLoading: rewardsCurrentlyLoading,
  };
};

export default connect(mapStateToProp, { getCouponsAction, getRewardsAction })(Coupons);
